import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Divider,
  Avatar,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { useAccount } from '../../contexts/AccountContext';
import { useModal } from '../../contexts/ModalContext';
import api from '../../services/api';
import { useLoader } from '../../contexts/LoaderContext';

const AccountOverview = () => {
  const { openSuccessModal, openErrorModal, openConfirmModal, openNoChangesModal } = useModal();
  const { accountId, loading: contextLoading } = useAccount();
  const { showLoader, hideLoader } = useLoader();
  const [formData, setFormData] = useState({
    company_name: '',
    gstin: '',
    industry: '',
    address: '',
    state: '',
    pincode: ''
  });
  const [originalFormData, setOriginalFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [accountDetails, setAccountDetails] = useState(null);
  const [noChangesDialogOpen, setNoChangesDialogOpen] = useState(false);

  const industryOptions = [
    { value: 'IT', label: 'Information Technology' },
    { value: 'FIN', label: 'Finance' },
    { value: 'REA', label: 'Real Estate' },
    { value: 'OTH', label: 'Other' }
  ];

  const stateOptions = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu', 
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
  ];

  useEffect(() => {
    if (accountId) {
      fetchAccountDetails();
    }
  }, [accountId]);

  const fetchAccountDetails = async () => {
    if (!accountId) return;
    try {
      showLoader();
      const response = await api.get(`/account/details/${accountId}/`);
      if (response.status === 200) {
        const data = response.data;
        setAccountDetails(data);
        const initialData = {
          company_name: data.company_name || '',
          gstin: data.gstin || '',
          industry: data.industry || '',
          address: data.address || '',
          state: data.state || '',
          pincode: data.pincode || ''
        };
        setFormData(initialData);
        setOriginalFormData(initialData);
      }
    } catch (error) {
      openErrorModal('Failed to load account details. Please try again.');
    } finally {
      hideLoader();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleStateChange = (event, newValue) => {
    setFormData(prev => ({
      ...prev,
      state: newValue || ''
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.company_name.trim()) {
      newErrors.company_name = 'Company name is required';
    }
    if (!formData.gstin.trim()) {
      newErrors.gstin = 'GSTIN is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const detectChanges = () => {
    const changedFields = {};
    let hasChanges = false;
    Object.keys(formData).forEach(key => {
      if (formData[key] !== originalFormData[key]) {
        changedFields[key] = formData[key];
        hasChanges = true;
      }
    });
    return { changedFields, hasChanges };
  };

  const handleSubmit = async () => {
    if (!validateForm() || !accountId) return;
    const { changedFields, hasChanges } = detectChanges();
    if (!hasChanges) {
      openNoChangesModal();
      return;
    }

    try {
      showLoader();
      await api.post(`/account/details/${accountId}/`, changedFields);
      openSuccessModal('Account details updated successfully');
      setErrors({});
    } catch (error) {
      if (error.response?.status === 400 && error.response.data) {
        // Map API validation errors to fields
        const fieldErrors = {};
        Object.keys(error.response.data).forEach(key => {
          fieldErrors[key] = Array.isArray(error.response.data[key])
            ? error.response.data[key].join(', ')
            : error.response.data[key];
        });
        setErrors(fieldErrors);
      } else {
        openErrorModal('Failed to update account. Please try again.');
      }
    } finally {
      hideLoader();
    }
  };

  const handleCancel = () => {
    if (accountDetails) {
      setFormData({ ...originalFormData });
    }
    setErrors({});
  };

  const handleCloseNoChangesDialog = () => {
    setNoChangesDialogOpen(false);
  };

  if (contextLoading || loading) {
    showLoader();
  }

  if (!accountId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">
          Unable to load account details. Please try again later.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                Admin: {accountDetails?.owner || 'Not specified'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company Name"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                margin="normal"
                required
                error={!!errors.company_name}
                helperText={errors.company_name}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="GST Details"
                name="gstin"
                value={formData.gstin}
                onChange={handleChange}
                margin="normal"
                required
                error={!!errors.gstin}
                helperText={errors.gstin}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="industry-label">Industry</InputLabel>
                <Select
                  labelId="industry-label"
                  id="industry"
                  name="industry"
                  value={formData.industry}
                  label="Industry"
                  onChange={handleChange}
                >
                  {industryOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={12}>
              <TextField
                fullWidth
                label="Complete Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                margin="normal"
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                id="state-select"
                options={stateOptions}
                value={formData.state}
                onChange={handleStateChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="State"
                    margin="normal"
                    fullWidth
                  />
                )}
                freeSolo
                disableClearable={false}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="PIN Code"
                name="pincode"
                value={formData.pincode}
                onChange={handleChange}
                margin="normal"
                error={!!errors.pincode}
                helperText={errors.pincode}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              sx={{ mr: 2 }}
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Dialog open={noChangesDialogOpen} onClose={handleCloseNoChangesDialog}>
        <DialogTitle>No Changes Detected</DialogTitle>
        <DialogContent>
          <Typography>No changes were made to the account details.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNoChangesDialog} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountOverview;
